import { useState } from 'react';
import { Box, Paper, Button, Fade, Typography, useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ThemeProvider, useTheme } from '@mui/material/styles';

import { colors } from '@src/styles';
import { selectDarkTheme } from '@src/styles/darkTheme';
import { fadeInUpSX, slideInRightSX } from '@src/styles/animations';
import { useTasksContext } from './context/TasksContext';
import {
  DateRangeField,
  ListView,
  TaskFilters,
  TaskDetailsPanel,
  ViewToggle,
  GroupsView,
  AnalyticsView,
  StatusProgressBar,
} from './components';

const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";

const FILTER_CONTROL_HEIGHT = 36;

export const TasksView = () => {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    selectedTaskId,
    isLoading,
    tasksSummary,
    newTasksCount,
    handleLoadNewTasks,
    selectedGroup,
    clearSelectedTask,
  } = useTasksContext();

  const [view, setView] = useState('table');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isGroupDetailView = view === 'groups' && selectedGroup !== null;
  const isDetailsOpen = Boolean(selectedTaskId) && view !== 'analytics';

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: string | null) => {
    if (newView !== null) {
      setView(newView);
      clearSelectedTask();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <ThemeProvider theme={selectDarkTheme}>
        <Paper
          elevation={0}
          sx={{
            height: isMobile ? 'auto' : 'calc(100vh - 56px)',
            overflow: isMobile ? 'visible' : 'hidden',
            padding: isMobile ? '12px 4px 16px' : '18px 24px 16px',
            margin: '0px',
            gap: '12px',
            display: 'flex',
            flexDirection: 'column',
            color: colors.primary.white,
            backgroundColor: colors.background.primary,
            backgroundImage: 'none',
            border: 'none',
            position: 'relative',
          }}
        >
          {!isGroupDetailView && (
            <>
              <Box sx={fadeInUpSX()}>
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: titleFontFamily,
                    fontSize: '24px',
                    fontWeight: 500,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.1,
                    color: colors.primary.white,
                    marginBottom: '2px',
                  }}
                >
                  Tasks
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: colors.brand.textSecondary }}>
                  Erros e avisos emitidos pelos fluxos de Solutions, triados por organização.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  backgroundColor: colors.brand.surface,
                  border: `1px solid ${colors.brand.line}`,
                  borderRadius: '10px',
                  padding: '8px 10px',
                  ...fadeInUpSX(60),
                }}
              >
                <Box
                  sx={{
                    flex: '999 1 420px',
                    minWidth: 0,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    '& .MuiFormControl-root': { margin: 0 },
                    '& .MuiInputBase-root': { height: FILTER_CONTROL_HEIGHT, fontSize: '13px' },
                    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { fontSize: '13px', top: '-2px' },
                    [theme.breakpoints.down('sm')]: {
                      '& > .MuiButton-root, & > .MuiBox-root': { width: '100%' },
                      '& .MuiFormControl-root, & .MuiAutocomplete-root': {
                        flex: '1 1 calc(50% - 8px)',
                        width: 'auto',
                        minWidth: 0,
                      },
                    },
                  }}
                >
                  <DateRangeField
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                  />
                  <TaskFilters />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Fade in={newTasksCount > 0}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={handleLoadNewTasks}
                      sx={{
                        display: newTasksCount > 0 ? 'flex' : 'none',
                        backgroundColor: 'rgba(255, 160, 21, 0.1)',
                        border: `1px solid ${colors.primary.yellow}`,
                        color: colors.primary.yellow,
                        fontWeight: 500,
                        textTransform: 'none',
                        padding: '6px 16px',
                        borderRadius: '6px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 160, 21, 0.18)',
                        },
                      }}
                    >
                      {newTasksCount} {newTasksCount === 1 ? 'nova task' : 'novas tasks'}
                    </Button>
                  </Fade>
                  <ViewToggle view={view} onViewChange={handleViewChange} />
                </Box>
              </Box>
              <StatusProgressBar tasksSummary={tasksSummary} isLoading={isLoading} />
            </>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '12px',
              flex: 1,
              minHeight: 0,
              marginBottom: isMobile ? 0 : '24px',
              ...fadeInUpSX(120),
            }}
          >
            <Box
              sx={{
                display: isMobile && isDetailsOpen ? 'none' : 'flex',
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                height: isMobile ? '70vh' : 'auto',
                overflow: 'hidden',
              }}
            >
              {view === 'table' && <ListView />}
              {view === 'groups' && <GroupsView />}
              {view === 'analytics' && <AnalyticsView />}
            </Box>
            {isDetailsOpen && (
              <Box
                sx={{
                  display: 'flex',
                  width: isMobile ? '100%' : '32%',
                  minWidth: isMobile ? 0 : 360,
                  minHeight: 0,
                  ...(isMobile ? fadeInUpSX() : slideInRightSX),
                }}
              >
                <TaskDetailsPanel />
              </Box>
            )}
          </Box>
        </Paper>
      </ThemeProvider>
    </LocalizationProvider>
  );
};
